/**
 * The client-side index of background DISPLAY images, built from the v3 catalog
 * (issue #567, B3).
 *
 * Every url the editor shows for a background used to be found by matching
 * substrings in a filename: `/<product>` for the primary, `-mini` / `-outdated`
 * for the variants, `_blacklogo` & friends for the style markers, and a
 * `tiles`→`gdrive_thumbnails` + `.png`→`.webp` rewrite for the selector
 * thumbnail. That only works while the bytes live under Kraken's naming scheme.
 * Phase B re-hosts them under content-hashed keys (`cai/baker/bg/<sha>.png`),
 * where a filename says nothing at all — so the catalog carries the structure
 * explicitly and the client reads it here instead of re-deriving it.
 *
 * The index is keyed (variation → product code) because the ALL-products
 * "What's New" view renders one banner per product from child Setting providers,
 * each of which resolves its OWN product's image — the same reason the tile cache
 * is product-agnostic (#251).
 */

/** One product's display image for one variation, as the client renders it. */
export interface BgImageEntry {
  /** The image shown for this product. "" when the product has only an -outdated file. */
  url: string;
  /** The `-mini` variant, or null. */
  miniUrl: string | null;
  /** The `-outdated` fallback, or null. */
  outdatedUrl: string | null;
  /** Selector-thumbnail (webp) for `url`; "" when there's no `url`. */
  thumbnailUrl: string;
  /** Style markers (blacklogo, whitelogoinverted, …) — persisted, not parsed. */
  markers: string[];
}

/** variation (1-based) → product code (plus "default") → entry. */
export type BgCatalogIndex = Record<number, Record<string, BgImageEntry>>;

/** The subset of a GET /api/v3/backgrounds item this index is built from. */
export interface CatalogBackgroundLike {
  /** 1-based display position once an admin has published an arrangement. */
  variation: number;
  /** The catalog variation behind the position; equal to it until a publish. */
  sourceVariation?: number | null;
  imageUrl: string;
  thumbnailUrl: string;
  miniUrl: string | null;
  outdatedUrl: string | null;
  markers: string[];
  /** Every product's image — sent only for a product=ALL request. */
  productImages?: Record<string, BgImageEntry>;
}

/**
 * Reshape the catalog list into the (variation → product) index.
 *
 * A single-product response carries just that product's display image, so it is
 * filed under `productValue`; an ALL response carries `productImages` for every
 * product and is used as-is (its key order is preserved — "default" first, which
 * is the tile the ALL selector thumbnails off).
 *
 * Returns null for an absent/empty list so callers can tell "not loaded yet"
 * from "loaded, this variation has no image for this product".
 */
export function buildBgCatalogIndex(
  catalogBackgrounds: CatalogBackgroundLike[] | null | undefined,
  productValue: string | null | undefined
): BgCatalogIndex | null {
  if (!Array.isArray(catalogBackgrounds) || catalogBackgrounds.length === 0) return null;

  const index: BgCatalogIndex = {};
  for (const b of catalogBackgrounds) {
    // Keyed by identity: the same value `bgOption` holds (bgIdentity.ts).
    const key = b.variation;
    if (!Number.isInteger(key) || key < 1) continue;

    if (b.productImages) {
      index[key] = b.productImages;
      continue;
    }
    if (!productValue) continue;
    // An -outdated-only product has no `imageUrl` but must still resolve (the
    // legacy path fell back to the -outdated file), so key off "any url".
    if (!b.imageUrl && !b.outdatedUrl && !b.miniUrl) continue;
    index[key] = {
      [productValue]: {
        url: b.imageUrl,
        miniUrl: b.miniUrl,
        outdatedUrl: b.outdatedUrl,
        thumbnailUrl: b.thumbnailUrl,
        markers: b.markers ?? [],
      },
    };
  }

  return index;
}

/**
 * One product's entry for a variation, or null. "ALL" is deliberately never
 * resolvable: it isn't a product, and the legacy `findBgUrl("/ALL")` found
 * nothing either — the ALL view resolves each child product separately.
 */
export function lookupBgImage(
  index: BgCatalogIndex | null | undefined,
  variation: number,
  productValue: string | null | undefined
): BgImageEntry | null {
  if (!index || !productValue || productValue === "ALL") return null;
  return index[variation]?.[productValue] ?? null;
}

/**
 * The variation's first entry — what the ALL-products selector shows on its tile
 * button. Legacy took the first PNG of the tileset, which was the `#<hash>`
 * default file; the catalog preserves that ordering ("default" first).
 */
export function firstBgImage(
  index: BgCatalogIndex | null | undefined,
  variation: number
): BgImageEntry | null {
  const entries = index?.[variation];
  if (!entries) return null;
  const first = Object.values(entries)[0];
  return first ?? null;
}
